/**
 * Navigation State
 * Provides reactive navigation sections with dynamic badges (e.g., active jobs count)
 * Uses URL-based routing: /p/{projectId}/... and /o/{orgId}/...
 * Uses WebSocket for real-time updates with polling as fallback
 */

import { computedOf, prop, type Prop } from '@tempots/dom'
import { createProjectJobs } from '@/services/gateway'
import type { JobsService } from '@/services/jobs.service'
import type { NavSection } from '@/data/types'
import { Workspace } from './workspace'
import {
  getWebSocketClient,
  type WebSocketMessage,
  type WebSocketClient,
} from '@/services/websocket'

// ============================================================================
// State
// ============================================================================

/* eslint-disable tempots/no-module-level-signals -- intentional global state for app */
/** Active jobs count (for badge display) */
export const activeJobsCount: Prop<number> = prop<number>(0)

/** Reactive navigation sections */
export const navSections = computedOf(
  Workspace.organization,
  Workspace.project,
  activeJobsCount
)((organization, project, jobsCount) => {
  const sections = [] as NavSection[]

  if (project != null) {
    const projectId = project.id
    const projectItems = [
      {
        href: `/p/${projectId}`,
        label: 'Overview',
        iconName: 'mdi:view-dashboard',
      },
      {
        href: `/p/${projectId}/files`,
        label: 'Files',
        iconName: 'mdi:folder',
      },
      {
        href: `/p/${projectId}/api-keys`,
        label: 'API Keys',
        iconName: 'mdi:key',
      },
      {
        href: `/p/${projectId}/jobs`,
        label: 'Jobs',
        iconName: 'mdi:clock-outline',
        badge: jobsCount > 0 ? jobsCount : undefined,
      },
      {
        href: `/p/${projectId}/actions`,
        label: 'Actions',
        iconName: 'mdi:lightning-bolt',
      },
    ]

    if (
      !project.isDefault ||
      (organization != null && !organization.isPersonal)
    ) {
      projectItems.push({
        href: `/p/${projectId}/members`,
        label: 'Members',
        iconName: 'mdi:account-group',
      })
    }

    sections.push({
      title: 'Project',
      items: projectItems,
    })
  }

  if (organization != null) {
    const orgId = organization.id
    const orgItems = [
      {
        href: `/o/${orgId}`,
        label: 'Overview',
        iconName: 'mdi:office-building',
      },
      {
        href: `/o/${orgId}/billing`,
        label: 'Billing',
        iconName: 'mdi:credit-card',
      },
    ]

    // Only show Members link for non-personal organizations
    if (!organization.isPersonal) {
      orgItems.push({
        href: `/o/${orgId}/members`,
        label: 'Members',
        iconName: 'mdi:account-group',
      })
    }

    sections.push({
      title: 'Organization',
      items: orgItems,
    })
  }

  // Management section (always shown)
  const managementItems = [
    {
      href: '/organizations',
      label: 'Organizations',
      iconName: 'mdi:domain',
    },
  ]
  if (organization != null) {
    managementItems.push({
      // Link to org-scoped projects if org selected, otherwise to global projects page
      href: `/o/${organization.id}/projects`,
      label: 'Projects',
      iconName: 'mdi:folder-multiple',
    })
  }
  sections.push({
    title: 'Management',
    items: managementItems,
  })

  return sections
})

// ============================================================================
// Actions
// ============================================================================

/** Whether we're currently fetching jobs count */
let isFetching = false

/** Cache jobs service per project */
let cachedProjectId = ''
let cachedJobsService: JobsService | null = null

function getJobsService(): JobsService | null {
  const projectId = Workspace.project.value.id
  if (projectId !== cachedProjectId) {
    cachedProjectId = projectId
    cachedJobsService = createProjectJobs(projectId)
  }
  return cachedJobsService
}

/**
 * Fetch and update the active jobs count
 * Polls for running/pending jobs and updates the sidebar badge
 * Badge is automatically reflected in navSections via computedOf
 */
export async function refreshActiveJobsCount(): Promise<void> {
  if (isFetching) return
  isFetching = true

  try {
    const jobsService = getJobsService()
    if (!jobsService) {
      // No project selected, reset count
      activeJobsCount.set(0)
      return
    }

    // Fetch jobs with running/pending status
    const [pendingResult, runningResult] = await Promise.allSettled([
      jobsService.list({ status: 'pending', pageSize: 1 }),
      jobsService.list({ status: 'processing', pageSize: 1 }),
    ])

    // Count active jobs from pagination totals
    let count = 0
    if (
      pendingResult.status === 'fulfilled' &&
      pendingResult.value.pagination
    ) {
      count += pendingResult.value.pagination.totalItems
    }
    if (
      runningResult.status === 'fulfilled' &&
      runningResult.value.pagination
    ) {
      count += runningResult.value.pagination.totalItems
    }

    // Update count - navSections will automatically recompute
    activeJobsCount.set(count)
  } catch (error) {
    console.error('Failed to fetch active jobs count:', error)
  } finally {
    isFetching = false
  }
}

/**
 * Start polling for active jobs count
 * @param intervalMs - Polling interval in milliseconds (default: 30 seconds)
 * @returns Cleanup function to stop polling
 */
export function startJobsCountPolling(intervalMs = 30000): () => void {
  // Initial fetch
  refreshActiveJobsCount()

  // Set up polling
  const intervalId = setInterval(() => {
    refreshActiveJobsCount()
  }, intervalMs)

  // Return cleanup function
  return () => {
    clearInterval(intervalId)
  }
}

// ============================================================================
// WebSocket Real-Time Updates
// ============================================================================

let wsClient: WebSocketClient | null = null
let wsMessageUnsub: (() => void) | null = null
let wsConnectUnsub: (() => void) | null = null
let pollingCleanup: (() => void) | null = null
let currentWsProjectId: string | null = null

/**
 * Handle incoming WebSocket messages
 */
function handleWebSocketMessage(message: WebSocketMessage): void {
  switch (message.type) {
    case 'job_update':
      // Job status changed - refresh the count
      refreshActiveJobsCount()
      break

    case 'jobs_count':
      // Direct count update from server
      if (typeof message.count === 'number') {
        activeJobsCount.set(message.count)
      }
      break
  }
}

/**
 * Start WebSocket subscription for real-time job updates
 * Falls back to polling if WebSocket connection fails
 * @param projectId - The project to subscribe to
 * @returns Cleanup function
 */
export function startRealtimeJobUpdates(projectId: string): () => void {
  // Clean up any existing connection
  stopRealtimeJobUpdates()

  currentWsProjectId = projectId

  // Get WebSocket client
  wsClient = getWebSocketClient()

  // Listen for messages
  wsMessageUnsub = wsClient.on('message', handleWebSocketMessage)

  // On connect, subscribe to project
  wsConnectUnsub = wsClient.on('connect', () => {
    if (currentWsProjectId) {
      wsClient?.subscribeToProject(currentWsProjectId)
    }
    // Stop polling when WebSocket is connected
    if (pollingCleanup) {
      pollingCleanup()
      pollingCleanup = null
    }
  })

  // On disconnect, start polling as fallback
  const disconnectUnsub = wsClient.on('disconnect', () => {
    if (!pollingCleanup) {
      pollingCleanup = startJobsCountPolling(30000)
    }
  })

  // Connect to WebSocket (async, fire-and-forget - handles errors internally)
  void wsClient.connect()

  // Also subscribe immediately if already connected
  if (wsClient.isConnected()) {
    wsClient.subscribeToProject(projectId)
  }

  // Start polling as fallback until WebSocket connects
  pollingCleanup = startJobsCountPolling(30000)

  // Return cleanup function
  return () => {
    disconnectUnsub()
    stopRealtimeJobUpdates()
  }
}

/**
 * Stop WebSocket subscription and polling
 */
export function stopRealtimeJobUpdates(): void {
  if (wsMessageUnsub) {
    wsMessageUnsub()
    wsMessageUnsub = null
  }
  if (wsConnectUnsub) {
    wsConnectUnsub()
    wsConnectUnsub = null
  }
  if (wsClient) {
    wsClient.unsubscribeFromProject()
    // Don't disconnect - the client might be used elsewhere
    wsClient = null
  }
  if (pollingCleanup) {
    pollingCleanup()
    pollingCleanup = null
  }
  currentWsProjectId = null
}
